import type { PETComposition, SpecificationListItem } from 'api';
import { Operand } from 'api';
import { translate } from 'i18n';
import type { ComponentProps } from 'react';
import { formatNumber } from 'ui';

import { MaterialBreakdownItem } from '../../components/MaterialBreakdownItem/MaterialBreakdownItem';
import { formatPercentageFeature } from '../formatFeature/formatFeature';
import { formatInstructionWarning } from '../formatInstructionWarning/formatInstructionWarning';
import { getTotalNonPet } from './getTotalNonPet';
import { getTotalPet } from './getTotalPet';

type MaterialItemDisplayData = {
  rawValue: number;
  value: string;
  secondaryValue: string;
  warning?: string;
  highlight?: boolean;
};

type CompositionItem = ComponentProps<typeof MaterialBreakdownItem>;

const shouldShowItem = (item: MaterialItemDisplayData | null | undefined, forceShow = false) => {
  if (!item) {
    return false;
  }
  if (forceShow) {
    return true;
  }
  if (item.warning || item.highlight) {
    return true;
  }
  return item.rawValue !== 0;
};

const buildMaterialItemDisplayData = (
  value: number | null | undefined,
  totalWeight: number,
  operand: Operand,
  instructions?: SpecificationListItem[]
): MaterialItemDisplayData | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const weightInKg = value * totalWeight;
  const formattedWeight =
    weightInKg > 0 && weightInKg < 0.01
      ? '<0.01kg'
      : formatNumber(weightInKg, { maximumFractionDigits: 2, minimumFractionDigits: 2, style: 'kilogram' });

  const instruction = formatInstructionWarning(operand, instructions);
  return {
    highlight: instruction?.highlight ?? false,
    rawValue: value,
    secondaryValue: formatPercentageFeature(value),
    value: formattedWeight,
    warning: instruction?.text,
  };
};

export const buildPETAiSampleCompositionItems = (
  composition: PETComposition,
  totalWeight: number,
  instructions?: SpecificationListItem[],
  options: { includeAllItems?: boolean } = {}
) => {
  const { includeAllItems = false } = options;
  const totalPet = getTotalPet(composition);
  const totalNonPet = getTotalNonPet(composition);

  const componentDisplayData = {
    brown: buildMaterialItemDisplayData(composition.brown, totalWeight, Operand.brown, instructions),
    clear: buildMaterialItemDisplayData(composition.clear, totalWeight, Operand.clear, instructions),
    clearNonFoodGrade: buildMaterialItemDisplayData(
      composition.clear_non_food_grade,
      totalWeight,
      Operand.clear_non_food_grade,
      instructions
    ),
    clearPetTray: buildMaterialItemDisplayData(
      composition.clear_pet_tray,
      totalWeight,
      Operand.clear_pet_tray,
      instructions
    ),
    darkBlue: buildMaterialItemDisplayData(composition.dark_blue, totalWeight, Operand.dark_blue, instructions),
    film: buildMaterialItemDisplayData(composition.film, totalWeight, Operand.film, instructions),
    fullSleeve: buildMaterialItemDisplayData(composition.full_sleeve, totalWeight, Operand.full_sleeve, instructions),
    green: buildMaterialItemDisplayData(composition.green, totalWeight, Operand.green, instructions),
    lightBlue: buildMaterialItemDisplayData(composition.light_blue, totalWeight, Operand.light_blue, instructions),
    metal: buildMaterialItemDisplayData(composition.metal, totalWeight, Operand.metal, instructions),
    nonPlastic: buildMaterialItemDisplayData(composition.non_plastic, totalWeight, Operand.non_plastic, instructions),
    oilBottle: buildMaterialItemDisplayData(composition.oil_bottle, totalWeight, Operand.oil_bottle, instructions),
    otherColour: buildMaterialItemDisplayData(
      composition.other_colour,
      totalWeight,
      Operand.other_colour,
      instructions
    ),
    otherPlastic: buildMaterialItemDisplayData(
      composition.other_plastic,
      totalWeight,
      Operand.other_plastic,
      instructions
    ),
    petTrayContaminatedClear: buildMaterialItemDisplayData(
      composition.pet_tray_contaminated_clear,
      totalWeight,
      Operand.pet_tray_contaminated_clear,
      instructions
    ),
    polypropylene: buildMaterialItemDisplayData(
      composition.polypropylene,
      totalWeight,
      Operand.polypropylene,
      instructions
    ),
    prohibitive: buildMaterialItemDisplayData(composition.prohibitive, totalWeight, Operand.prohibitive, instructions),
    totalNonPet: {
      label: translate('backlog.breakdowns.materialLabels.nonPet', { namespace: 'features' }),
      secondaryValue: formatPercentageFeature(totalNonPet),
      value: (() => {
        const weightInKg = totalNonPet * totalWeight;
        return weightInKg > 0 && weightInKg < 0.01
          ? '<0.01kg'
          : formatNumber(weightInKg, { maximumFractionDigits: 2, minimumFractionDigits: 2, style: 'kilogram' });
      })(),
    },
    totalPet: {
      label: translate('backlog.breakdowns.materialLabels.pet', { namespace: 'features' }),
      secondaryValue: formatPercentageFeature(totalPet),
      value: (() => {
        const weightInKg = totalPet * totalWeight;
        return weightInKg > 0 && weightInKg < 0.01
          ? '<0.01kg'
          : formatNumber(weightInKg, { maximumFractionDigits: 2, minimumFractionDigits: 2, style: 'kilogram' });
      })(),
    },
    white: buildMaterialItemDisplayData(composition.white, totalWeight, Operand.white, instructions),
  };

  const targetPetItems: CompositionItem[] = [];
  const nonTargetItems: CompositionItem[] = [];

  targetPetItems.push({
    dataTestId: 'material-breakdown-pet',
    label: componentDisplayData.totalPet.label,
    secondaryValue: componentDisplayData.totalPet.secondaryValue,
    value: componentDisplayData.totalPet.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.clear, includeAllItems)) {
    const clear = componentDisplayData.clear;
    if (clear) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-clear',
        highlight: clear.highlight,
        label: translate('backlog.breakdowns.materialLabels.clear', { namespace: 'features' }),
        secondaryValue: clear.secondaryValue,
        value: clear.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.clearPetTray, includeAllItems)) {
    const clearPetTray = componentDisplayData.clearPetTray;
    if (clearPetTray) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-clear-pet-tray',
        highlight: clearPetTray.highlight,
        label: translate('backlog.breakdowns.materialLabels.clearPetTray', { namespace: 'features' }),
        secondaryValue: clearPetTray.secondaryValue,
        value: clearPetTray.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.petTrayContaminatedClear, includeAllItems)) {
    const petTrayContaminatedClear = componentDisplayData.petTrayContaminatedClear;
    if (petTrayContaminatedClear) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-pet-tray-contaminated-clear',
        highlight: petTrayContaminatedClear.highlight,
        label: translate('backlog.breakdowns.materialLabels.petTrayContaminatedClear', { namespace: 'features' }),
        secondaryValue: petTrayContaminatedClear.secondaryValue,
        value: petTrayContaminatedClear.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.lightBlue, includeAllItems)) {
    const lightBlue = componentDisplayData.lightBlue;
    if (lightBlue) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-light-blue',
        highlight: lightBlue.highlight,
        label: translate('backlog.breakdowns.materialLabels.lightBlue', { namespace: 'features' }),
        secondaryValue: lightBlue.secondaryValue,
        value: lightBlue.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.white, includeAllItems)) {
    const white = componentDisplayData.white;
    if (white) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-white',
        highlight: white.highlight,
        label: translate('backlog.breakdowns.materialLabels.white', { namespace: 'features' }),
        secondaryValue: white.secondaryValue,
        value: white.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.brown, includeAllItems)) {
    const brown = componentDisplayData.brown;
    if (brown) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-brown',
        highlight: brown.highlight,
        label: translate('backlog.breakdowns.materialLabels.brown', { namespace: 'features' }),
        secondaryValue: brown.secondaryValue,
        value: brown.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.darkBlue, includeAllItems)) {
    const darkBlue = componentDisplayData.darkBlue;
    if (darkBlue) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-dark-blue',
        highlight: darkBlue.highlight,
        label: translate('backlog.breakdowns.materialLabels.darkBlue', { namespace: 'features' }),
        secondaryValue: darkBlue.secondaryValue,
        value: darkBlue.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.green, includeAllItems)) {
    const green = componentDisplayData.green;
    if (green) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-green',
        highlight: green.highlight,
        label: translate('backlog.breakdowns.materialLabels.green', { namespace: 'features' }),
        secondaryValue: green.secondaryValue,
        value: green.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.otherColour, includeAllItems)) {
    const otherColour = componentDisplayData.otherColour;
    if (otherColour) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-other',
        highlight: otherColour.highlight,
        label: translate('backlog.breakdowns.materialLabels.other', { namespace: 'features' }),
        secondaryValue: otherColour.secondaryValue,
        value: otherColour.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.clearNonFoodGrade, includeAllItems)) {
    const clearNonFoodGrade = componentDisplayData.clearNonFoodGrade;
    if (clearNonFoodGrade) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-clear-non-food-grade',
        highlight: clearNonFoodGrade.highlight,
        label: translate('backlog.breakdowns.materialLabels.clearNonFoodGrade', { namespace: 'features' }),
        secondaryValue: clearNonFoodGrade.secondaryValue,
        value: clearNonFoodGrade.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.fullSleeve, includeAllItems)) {
    const fullSleeve = componentDisplayData.fullSleeve;
    if (fullSleeve) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-full-sleeve',
        highlight: fullSleeve.highlight,
        label: translate('backlog.breakdowns.materialLabels.fullSleeve', { namespace: 'features' }),
        secondaryValue: fullSleeve.secondaryValue,
        value: fullSleeve.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.oilBottle, includeAllItems)) {
    const oilBottle = componentDisplayData.oilBottle;
    if (oilBottle) {
      targetPetItems.push({
        dataTestId: 'material-breakdown-oil-bottle',
        highlight: oilBottle.highlight,
        label: translate('backlog.breakdowns.materialLabels.oilBottle', { namespace: 'features' }),
        secondaryValue: oilBottle.secondaryValue,
        value: oilBottle.value,
      });
    }
  }

  nonTargetItems.push({
    dataTestId: 'material-breakdown-non-pet',
    label: componentDisplayData.totalNonPet.label,
    secondaryValue: componentDisplayData.totalNonPet.secondaryValue,
    value: componentDisplayData.totalNonPet.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.film, includeAllItems)) {
    const film = componentDisplayData.film;
    if (film) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-film',
        highlight: film.highlight,
        label: translate('backlog.breakdowns.materialLabels.film', { namespace: 'features' }),
        secondaryValue: film.secondaryValue,
        value: film.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.otherPlastic, includeAllItems)) {
    const otherPlastic = componentDisplayData.otherPlastic;
    if (otherPlastic) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-other-plastic',
        highlight: otherPlastic.highlight,
        label: translate('backlog.breakdowns.materialLabels.otherPlastic', { namespace: 'features' }),
        secondaryValue: otherPlastic.secondaryValue,
        value: otherPlastic.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.nonPlastic, includeAllItems)) {
    const nonPlastic = componentDisplayData.nonPlastic;
    if (nonPlastic) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-non-plastic',
        highlight: nonPlastic.highlight,
        label: translate('backlog.breakdowns.materialLabels.nonPlastic', { namespace: 'features' }),
        secondaryValue: nonPlastic.secondaryValue,
        value: nonPlastic.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.metal, includeAllItems)) {
    const metal = componentDisplayData.metal;
    if (metal) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-metal',
        highlight: metal.highlight,
        label: translate('backlog.breakdowns.materialLabels.metal', { namespace: 'features' }),
        secondaryValue: metal.secondaryValue,
        value: metal.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.polypropylene, includeAllItems)) {
    const polypropylene = componentDisplayData.polypropylene;
    if (polypropylene) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-polypropylene',
        highlight: polypropylene.highlight,
        label: translate('backlog.breakdowns.materialLabels.polypropylene', { namespace: 'features' }),
        secondaryValue: polypropylene.secondaryValue,
        value: polypropylene.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.prohibitive, includeAllItems)) {
    const prohibitive = componentDisplayData.prohibitive;
    if (prohibitive) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-prohibitive',
        highlight: prohibitive.highlight,
        label: translate('backlog.breakdowns.materialLabels.prohibitive', { namespace: 'features' }),
        secondaryValue: prohibitive.secondaryValue,
        value: prohibitive.value,
      });
    }
  }

  return {
    allValues: componentDisplayData,
    nonTargetSection: {
      items: nonTargetItems,
    },
    targetPetSection: {
      items: targetPetItems,
    },
  };
};
